import { useEffect, useId, useRef, useState } from 'react';
import { ZoomInIcon } from '@radix-ui/react-icons';
import type { HomeworkBoardCorner } from '../domain/homework-board-focus';

const corners = [
  ['TOP_LEFT', '左上角'],
  ['TOP_RIGHT', '右上角'],
  ['BOTTOM_LEFT', '左下角'],
  ['BOTTOM_RIGHT', '右下角'],
] as const;

export function ClassroomZoomOptions({
  onSelect,
}: {
  readonly onSelect: (corner: HomeworkBoardCorner) => void;
}) {
  return corners.map(([corner, label]) => (
    <button type="button" role="menuitem" key={corner} onClick={() => onSelect(corner)}>
      {label}
    </button>
  ));
}

export function ClassroomZoomControl({
  zoomed,
  onRestore,
  onSelect,
}: {
  readonly zoomed: boolean;
  readonly onRestore: () => void;
  readonly onSelect: (corner: HomeworkBoardCorner) => void;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const close = (restoreFocus = false) => {
    setOpen(false);
    if (restoreFocus) trigger.current?.focus();
  };
  useEffect(() => {
    if (!open) return;
    menu.current?.querySelector('button')?.focus();
    const outside = (event: PointerEvent) => {
      if (event.target instanceof Node && !root.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('pointerdown', outside);
    return () => document.removeEventListener('pointerdown', outside);
  }, [open]);
  return (
    <div
      className="studio-zoom"
      ref={root}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) close();
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && open) {
          event.preventDefault();
          event.stopPropagation();
          close(true);
        }
      }}
    >
      <button
        type="button"
        ref={trigger}
        className={zoomed ? 'is-active' : ''}
        aria-label={zoomed ? '恢复全盘' : '选择棋盘放大区域'}
        aria-haspopup={zoomed ? undefined : 'menu'}
        aria-expanded={zoomed ? undefined : open}
        aria-controls={open ? menuId : undefined}
        onClick={() => {
          if (zoomed) {
            close();
            onRestore();
          } else setOpen(!open);
        }}
        onKeyDown={(event) => {
          if (!zoomed && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
            event.preventDefault();
            setOpen(true);
          }
        }}
      >
        <ZoomInIcon aria-hidden="true" />
        <span>{zoomed ? '全盘' : '放大'}</span>
      </button>
      {open ? (
        <div
          id={menuId}
          ref={menu}
          className="studio-zoom-options"
          role="menu"
          aria-label="选择棋盘放大区域"
          onKeyDown={(event) => {
            const items = Array.from(
              event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="menuitem"]'),
            );
            const index = items.indexOf(document.activeElement as HTMLButtonElement);
            const next =
              event.key === 'ArrowDown'
                ? (index + 1) % items.length
                : event.key === 'ArrowUp'
                  ? (index + items.length - 1) % items.length
                  : event.key === 'Home'
                    ? 0
                    : event.key === 'End'
                      ? items.length - 1
                      : null;
            if (next !== null) {
              event.preventDefault();
              items[next]?.focus();
            }
          }}
        >
          <ClassroomZoomOptions
            onSelect={(corner) => {
              onSelect(corner);
              close(true);
            }}
          />
        </div>
      ) : null}
    </div>
  );
}
